import { Button, Form, Label, FormGroup, Spinner, Input, Row, Col } from 'reactstrap';
import { AlertOnErrors } from '../../shared/alertOnErrors';
import { LoadingIndicator } from '../shared/LoadingIndicator';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { MainContainer } from '../shared/MainContainer';
import { useParams, useHistory } from 'react-router';
import { FormButtons } from '../shared/FormButtons';
import { ButtonAsync } from 'reactstrap-buttonasync';
import { useAsyncCallback } from 'react-use-async-callback';
import { useDriverMetric } from '../../api/main/driverMetrics/useDriverMetric';
import { useDeleteDriverMetricCallback } from '../../api/main/driverMetrics/useDeleteDriverMetricCallback';
import { ConditionalFragment } from 'react-conditionalfragment';
import { Banner } from '../shared/Banner';
import { Background } from '../shared/background/Background';

/**
 * Delete a driverMetric (profile) by locking them out permanently.
 */
export const DeleteDriverMetric = () => {
    const { t } = useTranslation();
    const { id } = useParams<{ id: string }>();
    const { data: { model }, isLoading, errors: loadErrors } = useDriverMetric(id);
    const [remove, { errors: removeErrors }] = useDeleteDriverMetricCallback();
    const history = useHistory();

    const [onRemoveFormSubmit, { isExecuting: isRemoving, errors: removeFormErrors }] = useAsyncCallback(async () => {
        if (!model) {
            return;
        }

        await remove(model.id);

        history.goBack();
    }, [model, history]);

    return (
        <Background>
            <Banner>
                <Row>
                    <Col>
                        <h1>{t('deleteDriverMetric.heading', 'Are you sure you want to delete this safety metric?')}</h1>
                    </Col>
                    <ConditionalFragment showIf={isLoading}>
                        <Col xs="auto">
                            <LoadingIndicator size="sm" />
                        </Col>
                    </ConditionalFragment>
                </Row>
            </Banner>

            <MainContainer>
                <AlertOnErrors errors={[loadErrors, removeFormErrors, removeErrors]} />

                <Form onSubmit={e => { e.preventDefault(); onRemoveFormSubmit(); }}>
                    <FormGroup>
                        <Label htmlFor="firstName">{t('deleteDriverMetric.name', 'Name')}</Label>
                        <Input name="firstName" type="text" readOnly plaintext value={model?.name ?? ''} />
                    </FormGroup>
                    
                    <FormButtons>
                        <ConditionalFragment showIf={!isLoading}>
                            <ButtonAsync color="danger" isExecuting={isRemoving}
                                executingChildren={<><Spinner size="sm" /> {t('common.deleting', 'Deleting...')}</>}>
                                <FontAwesomeIcon icon="trash" />
                                <> {t('common.delete', 'Delete')}</>
                            </ButtonAsync>
                        </ConditionalFragment>
                        <Button type="button" color="primary" outline onClick={e => history.goBack()}>
                            {t('common.cancel', 'Cancel')}
                        </Button>
                    </FormButtons>
                </Form>
            </MainContainer>
        </Background>
    );
};
